import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConvertLeadModal = ({ lead, inventoryUnits, onClose, onConvert }) => {
  const [tenantData, setTenantData] = useState({
    name: lead?.name || '',
    company: lead?.company || '',
    email: '',
    phone: '',
    monthlyRevenue: (lead?.seatsRequired * lead?.budgetPerSeat) || 0,
    lockinEndDate: '',
    riskLevel: 'low'
  });

  const [selectedUnits, setSelectedUnits] = useState([]);
  const [errors, setErrors] = useState({});

  const availableUnits = inventoryUnits?.filter(unit => unit?.status === 'vacant');

  const totalSeatsAllocated = selectedUnits?.reduce((sum, unitId) => {
    const unit = availableUnits?.find(u => u?.id === unitId);
    return sum + (unit?.seatCount || 0);
  }, 0);

  const handleUnitToggle = (unitId) => {
    setSelectedUnits(prev =>
      prev?.includes(unitId)
        ? prev?.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!tenantData?.name?.trim()) newErrors.name = 'Name is required';
    if (!tenantData?.email?.trim()) newErrors.email = 'Email is required';
    if (!tenantData?.phone?.trim()) newErrors.phone = 'Phone is required';
    if (!tenantData?.lockinEndDate) newErrors.lockinEndDate = 'Lock-in end date is required';
    if (selectedUnits?.length === 0) newErrors.units = 'Select at least one inventory unit';
    if (totalSeatsAllocated < lead?.seatsRequired) {
      newErrors.units = `Need ${lead?.seatsRequired} seats, only ${totalSeatsAllocated} allocated`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleConvert = () => {
    if (validateForm()) {
      onConvert({
        ...tenantData,
        seatsTaken: totalSeatsAllocated,
        allocatedUnits: selectedUnits
      });
      onClose();
    }
  };

  const riskOptions = [
    { value: 'low', label: 'Low Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'high', label: 'High Risk' }
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background bg-opacity-80 overflow-y-auto">
      <div className="bg-card rounded-lg border border-border shadow-brand-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success bg-opacity-10 flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground text-lg">
                Convert Lead to Tenant
              </h3>
              <p className="text-sm caption text-muted-foreground">
                {lead?.name} - {lead?.company}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[calc(100vh-300px)] overflow-y-auto">
          <div>
            <h4 className="font-heading font-semibold text-foreground text-base mb-4">
              Tenant Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tenant Name"
                type="text"
                value={tenantData?.name}
                onChange={(e) => setTenantData({ ...tenantData, name: e?.target?.value })}
                error={errors?.name}
                required
              />
              <Input
                label="Company"
                type="text"
                value={tenantData?.company}
                onChange={(e) => setTenantData({ ...tenantData, company: e?.target?.value })}
              />
              <Input
                label="Email"
                type="email"
                value={tenantData?.email}
                onChange={(e) => setTenantData({ ...tenantData, email: e?.target?.value })}
                error={errors?.email}
                required
              />
              <Input
                label="Phone"
                type="tel"
                value={tenantData?.phone}
                onChange={(e) => setTenantData({ ...tenantData, phone: e?.target?.value })}
                error={errors?.phone}
                required
              />
              <Input
                label="Monthly Revenue"
                type="number"
                value={tenantData?.monthlyRevenue}
                onChange={(e) => setTenantData({ ...tenantData, monthlyRevenue: parseFloat(e?.target?.value) })}
                description="Calculated from seats × budget"
              />
              <Input
                label="Lock-in End Date"
                type="date"
                value={tenantData?.lockinEndDate}
                onChange={(e) => setTenantData({ ...tenantData, lockinEndDate: e?.target?.value })}
                error={errors?.lockinEndDate}
                required
              />
              <Select
                label="Risk Level"
                options={riskOptions}
                value={tenantData?.riskLevel}
                onChange={(value) => setTenantData({ ...tenantData, riskLevel: value })}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-heading font-semibold text-foreground text-base">
                Allocate Inventory Units
              </h4>
              <div className="text-sm caption">
                <span className="text-muted-foreground">Required: </span>
                <span className="font-semibold data-text text-foreground">{lead?.seatsRequired} seats</span>
                <span className="text-muted-foreground mx-2">|</span>
                <span className="text-muted-foreground">Allocated: </span>
                <span className={`font-semibold data-text ${totalSeatsAllocated >= lead?.seatsRequired ? 'text-success' : 'text-warning'}`}>
                  {totalSeatsAllocated} seats
                </span>
              </div>
            </div>

            {errors?.units && (
              <div className="mb-4 p-3 bg-error bg-opacity-10 border border-error rounded-lg flex items-center gap-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <span className="text-sm text-error">{errors?.units}</span>
              </div>
            )}

            <div className="space-y-2 max-h-64 overflow-y-auto border border-border rounded-lg p-4">
              {availableUnits?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm caption text-muted-foreground">No vacant units available</p>
                </div>
              ) : (
                availableUnits?.map(unit => (
                  <div
                    key={unit?.id}
                    className="flex items-center justify-between p-3 bg-muted bg-opacity-30 rounded-lg hover:bg-opacity-50 transition-smooth"
                  >
                    <Checkbox
                      checked={selectedUnits?.includes(unit?.id)}
                      onChange={() => handleUnitToggle(unit?.id)}
                      label={
                        <div className="ml-3">
                          <p className="font-medium text-foreground text-sm">
                            {unit?.name} - Floor {unit?.floor}
                          </p>
                          <p className="text-xs caption text-muted-foreground">
                            {unit?.unitType} • {unit?.seatCount} seats • ₹{unit?.pricePerSeat?.toLocaleString('en-IN')}/seat
                          </p>
                        </div>
                      }
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConvert}
            iconName="CheckCircle"
            iconPosition="left"
            className="flex-1"
          >
            Convert to Tenant
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConvertLeadModal;